import { useState, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";

import { LANGUAGES, type LangCode } from "@/lib/languages";

/**
 * apps/web :: components/onboarding :: NativeLanguageStep
 *
 * Step 2 of the onboarding wizard. Radio-style grid of language cards —
 * each card displays the language's name in its own script (the primary
 * label, large) with the English name beneath in smaller type so users
 * who don't yet recognise their own script can still spot their language.
 *
 * Accessibility:
 *   - Container is `role="radiogroup"`.
 *   - Each card is a `role="radio"` with `aria-checked`.
 *   - Enter / Space selects a focused card.
 */
export interface NativeLanguageStepProps {
  value: LangCode | null;
  onNext: (code: LangCode) => void;
  onBack: () => void;
}

export default function NativeLanguageStep({
  value,
  onNext,
  onBack,
}: NativeLanguageStepProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<LangCode | null>(value);

  function handleKeyDown(
    ev: KeyboardEvent<HTMLButtonElement>,
    code: LangCode,
  ) {
    if (ev.key === "Enter" || ev.key === " ") {
      ev.preventDefault();
      setDraft(code);
    }
  }

  const canProceed = draft !== null;

  return (
    <div className="space-y-md">
      <header>
        <h2 className="font-display text-xl font-semibold text-brand-primary">
          {t("onboarding.nativeLanguage.title")}
        </h2>
        <p className="mt-xs text-sm text-neutral-700">
          {t("onboarding.nativeLanguage.body")}
        </p>
      </header>

      <div
        role="radiogroup"
        aria-label={t("onboarding.nativeLanguage.groupLabel")}
        className="grid grid-cols-1 gap-sm sm:grid-cols-2 lg:grid-cols-4"
      >
        {LANGUAGES.map((lang) => {
          const selected = draft === lang.code;
          return (
            <button
              key={lang.code}
              type="button"
              role="radio"
              aria-checked={selected}
              data-testid={`language-card-${lang.code}`}
              onClick={() => setDraft(lang.code)}
              onKeyDown={(ev) => handleKeyDown(ev, lang.code)}
              className={[
                "flex flex-col items-start gap-xs rounded-md border bg-white p-sm text-left transition-colors",
                "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary",
                selected
                  ? "border-brand-primary bg-brand-primary/5"
                  : "border-brand-primary/20 hover:border-brand-primary/40",
              ].join(" ")}
            >
              <span className="font-display text-lg font-semibold text-brand-primary">
                {lang.nativeName}
              </span>
              <span className="text-xs text-neutral-600">
                {lang.englishName}
              </span>
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between gap-sm pt-md">
        <button
          type="button"
          data-testid="onboarding-back"
          onClick={onBack}
          className="inline-flex items-center justify-center rounded-md border border-brand-primary/20 bg-white px-md py-sm text-sm font-medium text-brand-primary transition-colors hover:border-brand-primary/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
        >
          {t("onboarding.back")}
        </button>
        <button
          type="button"
          data-testid="onboarding-next"
          disabled={!canProceed}
          onClick={() => {
            if (draft !== null) onNext(draft);
          }}
          className="inline-flex items-center justify-center rounded-md bg-brand-primary px-md py-sm text-sm font-semibold text-white transition-colors hover:bg-brand-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary disabled:cursor-not-allowed disabled:opacity-50"
        >
          {t("onboarding.next")}
        </button>
      </div>
    </div>
  );
}
